import React, { useState, useEffect, useRef } from 'react';
import {
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  Menu,
  MenuItem,
  InputAdornment,
} from '@material-ui/core';
import { useSelector, useDispatch } from 'react-redux';
import {
  UPGRADE_CREATE,
  UPGRADE_EDIT,
  UPGRADE_VIEW,
} from '../../../../../constants/AccomodationConst';
import {
  RBAC_PERMISSION_LEVEL_FULL_ACCESS,
  RBAC_TYPE_HOTEL_ACCOMMODATIONS,
} from '../../../../../constants/RoleConsts';
import DeletePopupModel from '../../../../../shared/delete-popup/Delete-Popup';
import LinearProgressBar from '../../../../../shared/progress-bar/Progress';
import { DragIndicator, MoreHoriz } from '@material-ui/icons';
import TablePaginationActions from '../../../../../shared/table-pagination/TablePaginationActions';
import CreateIcon from '@material-ui/icons/Create';
import './Upgrade-List.scss';
import {
  allRoomTypesSelector,
  allUpgradesSelector,
  fetchAllAccommodations,
  setAllUpgrades,
} from '../../../../property/propertySlice';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import { deleteAccommodationUpgradeAPI } from '../../../../../utils/ServicesAPIUtils';
import { notificationManager } from '../../../../../utils/NotificationUtils';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import useOutsideAlerter from '../../../../../custom-hooks/useOutsideAlerter';
import { handleOnDragEnd } from '../../../../../shared/custom-dragdrop/dragDrop';
import SearchBar from '../../../../../shared/search-bar/Search-Bar';
import { createDataSortMapping } from '../../../../../utils/PropertyAPIUtils';
import { isDragDisabled } from '../../../../../utils/formUtils';
import { decryptLocalStorage } from '../../../../../utils/authUtils';

const UpgradeList = (props) => {
  const {
    onDrawerToggle,
    onViewToggle,
    selectedHotel,
    permissions,
    page,
    rowsPerPage,
    onsetRowsPerPage,
    onsetPage,
  } = props;
  const [selectedValue, setSelectedValue] = useState({
    id: '',
    name: '',
    title: '',
    description: '',
    status: false,
    anchorEl: null,
  });
  const originalUpgrades = useSelector(allUpgradesSelector);
  const originalRoomTypes = useSelector(allRoomTypesSelector);
  const [initalUpgrades, setInitalUpgrades] = useState([]);
  const [upgrades, setUpgrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const wrapperRef = useRef(null);
  const prevHotelIdRef = useRef(selectedHotel);
  const [popUp, setPopUp] = useState(false);
  const [visible, setVisible] = useState(false);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    // assigning data set to local list while keeping an orginal data set for filtering
    setUpgrades(originalUpgrades);
    const upgradeList = originalUpgrades.map((obj) => {
      const roomTypeObj = originalRoomTypes.find((r) => r.id === obj.roomTypeId);
      const returnObj = {
        ...obj,
        roomType: roomTypeObj ? roomTypeObj.name : '',
      };
      return returnObj;
    });
    setInitalUpgrades(upgradeList);
    setLoading(false);
    setSearchText('');
  }, [originalUpgrades]);

  useEffect(() => {
    if (loading || upgrades?.length === 0) return;
    const totalItems = upgrades?.length;
    const maxPageIndex = Math.max(Math.ceil(totalItems / rowsPerPage) - 1, 0);
    if (page > maxPageIndex) {
      onsetPage(maxPageIndex);
    }
  }, [upgrades?.length, rowsPerPage, page, onsetPage]);

  useEffect(() => {
    if (prevHotelIdRef?.current !== selectedHotel) {
      onsetPage(0);
    }
    prevHotelIdRef.current = selectedHotel;
  }, [selectedHotel]);

  const dragDisabled = isDragDisabled(upgrades, searchText);

  const handleSearchText = (searchText) => {
    setSearchText(searchText);
    const filteredRows = initalUpgrades.filter((item) => {
      return item.roomType.toLowerCase().includes(searchText.toLowerCase());
    });
    onsetPage(0);
    setUpgrades(filteredRows);
  };

  const handleChangePage = (event, newPage) => {
    onsetPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    onsetRowsPerPage(parseInt(event.target.value, 10));
    onsetPage(0);
  };

  const onOpenDrawer = (event, type) => {
    event.stopPropagation();
    onDrawerToggle(type, true, selectedValue.upgrade);
    handleClose();
  };

  const handleClose = () => {
    setSelectedValue({
      id: '',
      name: '',
      title: '',
      description: '',
      status: false,
      anchorEl: null,
      floor: null,
    });
  };

  const handleCellMenuClick = (event, upgrade) => {
    event.stopPropagation();
    setSelectedValue({
      id: upgrade.id,
      name: retrieveRoomType(upgrade) + ' Upgrade',
      title: '',
      description: '',
      status: false,
      anchorEl: event.currentTarget,
      upgrade: upgrade,
    });
  };

  const deleteAccommodationUpgrade = async (e) => {
    e.preventDefault();
    setSelectedValue((prev) => ({
      ...prev,
      title: 'Are you sure you want to delete',
      description: 'This action cannot be reversed',
      status: true,
      anchorEl: null,
    }));
  };

  const deleteUpgrade = async () => {
    try {
      const lang = decryptLocalStorage('lang');
      const delByLang = lang !== 'en' ? lang : '';
      if (selectedHotel && selectedValue && selectedValue.id) {
        await deleteAccommodationUpgradeAPI(selectedHotel, selectedValue.id, delByLang);
        dispatch(fetchAllAccommodations(selectedHotel));
        notificationManager('success', 'Upgrade deleted successfully');
      }
      handleClose();
    } catch (e) {
      if (e.graphQLErrors) {
        notificationManager('danger', e.graphQLErrors[0].message);
      } else {
        notificationManager('danger', 'Failed to delete Upgrade');
      }
      handleClose();
    }
  };

  const onDeleteCancel = () => {
    setSelectedValue({
      id: '',
      name: '',
      title: '',
      description: '',
      status: false,
      anchorEl: null,
    });
  };

  const onViewDetails = (view, e) => {
    if (e?.target?.tagName === 'TD') {
      onViewToggle(UPGRADE_VIEW, true, view);
    }
  };

  const onClickView = (e) => {
    if (selectedValue && selectedValue.upgrade) {
      onViewToggle(UPGRADE_VIEW, true, selectedValue.upgrade);
    }
  };

  const retrieveRoomType = (upgrade) => {
    return originalRoomTypes.find((roomType) => roomType.id === upgrade.roomTypeId)?.name;
  };

  const retriveUpgradedRoomtype = (upgrade) => {
    return originalRoomTypes
      .filter((roomType) =>
        upgrade.upgradeType.some((upgradetype) => upgradetype.upgradeType === roomType.id),
      )
      .map((item) => {
        return item.name;
      })
      .join(', ');
  };

  useOutsideAlerter(wrapperRef, visible, setPopUp);

  return (
    <div className='mt-4 mb-4 group-list-container'>
      {selectedValue.id && selectedValue.status === true && (
        <DeletePopupModel
          deletePopUp={selectedValue}
          onDelete={deleteUpgrade}
          onDeleteCancel={onDeleteCancel}
        />
      )}

      <SearchBar
        type={UPGRADE_CREATE}
        buttonName='Add Upgrade'
        displayAddOrEdit={
          permissions[RBAC_TYPE_HOTEL_ACCOMMODATIONS] === RBAC_PERMISSION_LEVEL_FULL_ACCESS
            ? 'show'
            : 'hide'
        }
        onOpenDrawer={onOpenDrawer}
        setSearchText={handleSearchText}
        visible={visible}
        setVisible={setVisible}
        popUp={popUp}
        setPopUp={setPopUp}
        placeHolder={upgrades}
        setPlaceHolder={setUpgrades}
        globalPlaceHolder={originalUpgrades}
        setGlobalPlaceHolder={setAllUpgrades}
        dispatch={dispatch}
        mappingApi={createDataSortMapping}
        displayText='Accommodations upgrades'
        module='ACCOMMODATION_UPGRADE'
        searchText={searchText}
        setSearch={setSearchText}
        actionMenuItems={[]}
      />

      <div ref={wrapperRef} className='row mx-2 d-flex mt-4'>
        {loading ? (
          <div className='pt-4 col-md-12'>
            <LinearProgressBar />
          </div>
        ) : (
          upgrades && (
            <TableContainer>
              <Table className='list-table' aria-label='simple table'>
                <TableHead>
                  <TableRow>
                    <TableCell></TableCell>
                    <TableCell className='w-30'>Room type name</TableCell>
                    <TableCell className='w-20'>No.of Upgrades</TableCell>
                    <TableCell className='w-40'>Upgrades Available</TableCell>
                    <TableCell align='right' className='w-10'></TableCell>
                  </TableRow>
                </TableHead>

                <DragDropContext
                  onDragEnd={(event) =>
                    handleOnDragEnd(
                      event,
                      setVisible,
                      upgrades,
                      setUpgrades,
                      false,
                      page,
                      rowsPerPage,
                    )
                  }
                  isDropDisabled={dragDisabled}
                >
                  <Droppable droppableId='spas' isDropDisabled={dragDisabled}>
                    {(provided) => (
                      <TableBody {...provided.droppableProps} ref={provided.innerRef}>
                        {(rowsPerPage > 0
                          ? upgrades.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          : upgrades
                        ).map((upgrade, i) => (
                          <Draggable
                            key={upgrade.id}
                            draggableId={upgrade.id}
                            index={i}
                            isDragDisabled={dragDisabled}
                          >
                            {(provided) => (
                              <TableRow
                                key={i}
                                className='border-white pointer'
                                onClick={(e) => {
                                  onViewDetails(upgrade, e);
                                }}
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <TableCell>
                                  {!dragDisabled && (
                                    <DragIndicator className='res-menu-accordion-drag' />
                                  )}
                                </TableCell>
                                <TableCell className='w-30'>{retrieveRoomType(upgrade)}</TableCell>
                                <TableCell className='w-20'>
                                  {upgrade?.upgradeType?.length}
                                </TableCell>
                                <TableCell className='w-40'>
                                  {retriveUpgradedRoomtype(upgrade)}
                                </TableCell>
                                <TableCell className='w-10' align='right'>
                                  {permissions[RBAC_TYPE_HOTEL_ACCOMMODATIONS] ===
                                    RBAC_PERMISSION_LEVEL_FULL_ACCESS && (
                                    <Button
                                      aria-controls='simple-menu'
                                      aria-haspopup='true'
                                      onClick={(e) => {
                                        handleCellMenuClick(e, upgrade);
                                      }}
                                      className='showpopupInner'
                                    >
                                      <MoreHoriz className='showpopupInner' />
                                    </Button>
                                  )}
                                  <Menu
                                    id={i}
                                    anchorEl={selectedValue?.anchorEl}
                                    disableRestoreFocus
                                    MenuListProps={{ autoFocusItem: false }}
                                    keepMounted
                                    open={
                                      Boolean(selectedValue.anchorEl) &&
                                      selectedValue.id === upgrade.id
                                    }
                                    onClose={handleClose}
                                    className='shadow'
                                  >
                                    <MenuItem
                                      className='pe-4'
                                      onClick={(e) => {
                                        onOpenDrawer(e, UPGRADE_EDIT);
                                      }}
                                    >
                                      <CreateIcon className='me-2 action-menu-icon' />
                                      Edit
                                    </MenuItem>
                                    <MenuItem
                                      className='pe-4'
                                      onClick={(e) => {
                                        onClickView(e);
                                      }}
                                    >
                                      <VisibilityIcon className='me-2 action-menu-icon' />
                                      View
                                    </MenuItem>
                                    <MenuItem
                                      className='pe-4'
                                      onClick={(e) => {
                                        deleteAccommodationUpgrade(e);
                                      }}
                                    >
                                      <DeleteForeverIcon className='me-2 action-menu-icon' />
                                      Delete
                                    </MenuItem>
                                  </Menu>
                                </TableCell>
                              </TableRow>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </TableBody>
                    )}
                  </Droppable>
                </DragDropContext>
                <TableFooter>
                  <TableRow className='paginator'>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                      count={upgrades.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      SelectProps={{
                        inputProps: { 'aria-label': 'rows per page' },
                        native: true,
                      }}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      ActionsComponent={TablePaginationActions}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          )
        )}
      </div>
    </div>
  );
};

export default UpgradeList;
